/**
 * Профиль ученика, который клиент сохраняет на сервере: множественный
 * выбор хобби. Хобби не влияют на уроки — только на темы беседы с квоккой.
 */

import { z } from 'zod';

import { HOBBIES } from '../hobbies';
import { COGNITIVE_PROFILES, type CognitiveProfile } from '../aptitude';
import type { CurriculumLevel } from './curriculum';
import type { Plan } from '../billing';
import type { LanguageCode } from './language';

export const MAX_HOBBIES = 8;

const HOBBY_IDS = new Set(HOBBIES.map((hobby) => hobby.id));

/** Список хобби: известные id, без повторов, от одного до `MAX_HOBBIES`. */
export const HobbiesSchema = z
  .array(z.string().refine((id) => HOBBY_IDS.has(id), { message: 'Неизвестное хобби' }))
  .min(1)
  .max(MAX_HOBBIES)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'Хобби повторяются' });

export const UpdateHobbiesSchema = z.object({
  userId: z.string().uuid(),
  hobbies: HobbiesSchema,
});
export type UpdateHobbies = z.infer<typeof UpdateHobbiesSchema>;

/** Имя для Тико: буквы, пробел, дефис и апостроф; без ссылок и цифр. */
export const UserNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[\p{L}][\p{L}\p{M}' -]*$/u);

export const UpdateUserNameSchema = z.object({ userName: UserNameSchema });
export type UpdateUserName = z.infer<typeof UpdateUserNameSchema>;

/** Профиль ученика для клиента. */
export interface ProfileView {
  readonly userId: string;
  /** Изучаемый язык, к которому относятся ступень, веса и ведомый режим. */
  readonly language: LanguageCode;
  readonly currentLevel: CurriculumLevel;
  readonly estimatedLevel: number;
  readonly inductiveWeight: number;
  readonly deductiveWeight: number;
  readonly hobbies: readonly string[];
  /** Имя, которым ученик представился Тико; `null` — ещё не знакомились. */
  readonly userName: string | null;
  /** Вводный тур по приложению уже показан — второй раз не открывается. */
  readonly hasSeenTutorial: boolean;
  /** Тариф, который ученик выбрал бы после беты; `null` — ещё не выбирал. */
  readonly planIntent: Plan | null;
  /** Входной тест пройден — повторно он не предлагается. */
  readonly placementDone: boolean;
  /** Результаты тестов способностей; `null` — ещё не проходил. */
  readonly aptitude: AptitudeView | null;
  /** Ведомый режим уроков (маленькие шаги и озвучка). */
  readonly guided: boolean;
}

export interface AptitudeView {
  readonly memory: number;
  readonly sound: number | null;
  readonly grammar: number;
  readonly profile: CognitiveProfile;
}

const Score = z.number().min(0).max(1);

/** Итоги трёх тестов способностей из онбординга. */
export const SubmitAptitudeSchema = z.object({
  userId: z.string().uuid(),
  memory: Score,
  sound: Score.nullable(),
  grammar: Score,
});
export type SubmitAptitude = z.infer<typeof SubmitAptitudeSchema>;

export const CognitiveProfileSchema = z.enum(COGNITIVE_PROFILES);

/** Ответ на отправку тестов: профиль и стартовый план уроков. */
export interface AptitudeResult {
  readonly profile: CognitiveProfile;
  readonly strategy: 'INDUCTIVE' | 'DEDUCTIVE';
  readonly guided: boolean;
  readonly inductiveWeight: number;
  readonly deductiveWeight: number;
}
